import React, { useMemo, useState } from 'react';
import { useDeals } from '../context/DataContext';
import { useBrandPitches } from '../context/BrandPitchesContext';
import { useAuth } from '../context/AuthContext';
import { Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from './ui/command';
import { PitchRow } from './PitchRow';
import { fmtMoney } from '../lib/format';

/**
 * TalentPitchDashboard — the brands a manager has flagged as this talent's top targets.
 * Star rating was removed; rows are now sorted by biggest deal £ so top-earning brands sit on top.
 */
const TalentPitchDashboard = ({ talentId }) => {
  const { user } = useAuth();
  const { brands, deals } = useDeals();
  const { forTalent, ensurePitch, updatePitch, removePitch, addComms, removeComms } = useBrandPitches();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const [addOpen, setAddOpen] = useState(false);
  const [q, setQ] = useState('');

  // Biggest deal value ever done for this (brand, talent), used both to sort and to show inline
  const dealValueByBrand = useMemo(() => {
    const map = {};
    (deals || []).forEach((d) => {
      const bid = d.brand_id;
      const tids = new Set();
      if (d.talent_id) tids.add(d.talent_id);
      (d.talent_ids || []).forEach((t) => t && tids.add(t));
      if (!bid || !tids.has(talentId)) return;
      const v = Number(d.value) || 0;
      if (v <= 0) return;
      map[bid] = Math.max(map[bid] || 0, v);
    });
    return map;
  }, [deals, talentId]);

  const rows = useMemo(() => {
    const base = forTalent(talentId);
    return [...base].sort((a, b) => (dealValueByBrand[b.brand_id] || 0) - (dealValueByBrand[a.brand_id] || 0));
  }, [forTalent, talentId, dealValueByBrand]);

  const brandsWithoutPitch = useMemo(() => {
    const taken = new Set(rows.map((r) => r.brand_id));
    return brands.filter((b) => !taken.has(b.id));
  }, [brands, rows]);

  const addBrandToPitch = async (brand_id) => {
    await ensurePitch(brand_id, talentId);
    setAddOpen(false); setQ('');
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="talent-pitch-dashboard">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-700">brand pitching</h3>
        <span className="text-xs text-slate-400">— sorted by biggest deal £</span>
        {isManager && (
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" className="ml-auto bg-pink-500 hover:bg-pink-600 text-white" data-testid="add-brand-pitch-btn">
                <Plus className="w-4 h-4 mr-1" /> add brand
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0 w-80">
              <Command>
                <CommandInput value={q} onValueChange={setQ} placeholder="search brands…" />
                <CommandList>
                  <CommandEmpty>no brand match</CommandEmpty>
                  <CommandGroup>
                    {brandsWithoutPitch.map((b) => (
                      <CommandItem key={b.id} value={b.name} onSelect={() => addBrandToPitch(b.id)}>
                        <span className="inline-flex items-center gap-2">
                          <span>{b.logo}</span>
                          <span>{b.name}</span>
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400">
          {isManager ? 'no brands added yet — click "add brand" to start building this talent\'s top targets' : 'nothing here yet'}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((row) => {
            const b = brands.find((x) => x.id === row.brand_id);
            const biggest = dealValueByBrand[row.brand_id];
            const leftContent = (
              <>
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-base shrink-0">{b?.logo || '📦'}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 truncate">{b?.name || '—'}</div>
                  {row.notes && (
                    <div className="text-[11px] text-slate-500 truncate mt-0.5" title={row.notes} data-testid={`pitch-note-${row.id}`}>
                      {row.notes}
                    </div>
                  )}
                </div>
              </>
            );
            return (
              <PitchRow
                key={row.id}
                row={row}
                leftContent={leftContent}
                canEdit={isManager}
                hideStars
                rightExtra={biggest ? (
                  <span className="text-[11px] font-semibold text-pink-600 bg-pink-50 rounded-full px-2 py-0.5" title="biggest deal £ for this brand">
                    {fmtMoney(biggest)}
                  </span>
                ) : null}
                onStatus={(v) => updatePitch(row.id, { status: v })}
                onRemove={() => { if (window.confirm('Remove this brand from the pitch list?')) removePitch(row.id); }}
                onAddComms={(text) => addComms(row.id, text)}
                onRemoveComms={(cid) => removeComms(row.id, cid)}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default TalentPitchDashboard;
